import React from "react";

import ThumbnailStrip from "@/components/OmniCine/Tracks/Shared/ThumbnailStrip";
import type { LaneRendererProps } from "../lane.types";
import { GLASS_SECONDARY_TEXT_CLASS, itemVisualDensity, overlapCounts, productTone, readNumberMeta, readStringMeta, visibleLaneItems } from "./rendering";

function stateChip(label: string, tone: "sky" | "amber" | "rose" | "slate") {
  const className =
    tone === "sky"
      ? "border-sky-700/16 bg-sky-50/92 text-sky-900"
      : tone === "amber"
        ? "border-amber-700/16 bg-amber-50/92 text-amber-900"
        : tone === "rose"
          ? "border-rose-700/16 bg-rose-50/92 text-rose-900"
          : "border-slate-700/16 bg-slate-50/92 text-slate-900";

  return <span className={["rounded-full border px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em]", className].join(" ")}>{label}</span>;
}

function contractStateChip(contractState: string | null | undefined) {
  switch (contractState) {
    case "proxy_stale":
      return stateChip("proxy stale", "amber");
    case "proxy_failed":
      return stateChip("proxy failed", "rose");
    case "proxy_missing":
      return stateChip("no proxy", "rose");
    case "deprecated_proxy_fallback":
      return stateChip("legacy proxy", "amber");
    case "direct_media_only":
      return stateChip("direct source", "amber");
    case "source_degraded":
      return stateChip("degraded source", "amber");
    case "offline":
      return stateChip("offline", "rose");
    case "missing":
      return stateChip("missing", "rose");
    default:
      return null;
  }
}

function VideoThumbnailBed({ src, durationSec }: { src: string; durationSec: number }) {
  return (
    <div className="absolute inset-0 overflow-hidden rounded-[inherit]">
      <ThumbnailStrip src={src} durationSec={Math.max(0.1, durationSec)} height={40} provider="video" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(186,230,253,0.16),rgba(14,165,233,0.14))]" />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(2,6,23,0.08),rgba(2,6,23,0.02),rgba(2,6,23,0.08))]" />
    </div>
  );
}

function VideoGeneratingOverlay({ widthPx, status }: { widthPx: number; status: "queued" | "generating" }) {
  const label = status === "queued" ? "render queued" : "render generating";
  const frameCount = Math.max(2, Math.min(6, Math.round(widthPx / 74)));

  return (
    <div className="pointer-events-none absolute inset-0 z-[1] overflow-hidden rounded-[inherit]">
      <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(15,23,42,0.30),rgba(251,191,36,0.12),rgba(15,23,42,0.26))]" />
      <div className="absolute inset-x-0 top-0 flex h-[6px] items-center justify-around bg-slate-950/42 px-2">
        {Array.from({ length: Math.max(6, frameCount * 2) }, (_, index) => (
          <div key={`top-${index}`} className="h-[2px] w-3 rounded-full bg-amber-100/44" />
        ))}
      </div>
      <div className="absolute inset-x-0 bottom-0 flex h-[6px] items-center justify-around bg-slate-950/42 px-2">
        {Array.from({ length: Math.max(6, frameCount * 2) }, (_, index) => (
          <div key={`bottom-${index}`} className="h-[2px] w-3 rounded-full bg-amber-100/44" />
        ))}
      </div>
      <div className="absolute inset-x-3 top-1/2 flex -translate-y-1/2 gap-2 opacity-90">
        {Array.from({ length: frameCount }, (_, index) => (
          <div
            key={index}
            className="min-w-0 flex-1 overflow-hidden rounded-md border border-amber-100/18 bg-slate-950/28"
          >
            <div className="h-2 border-b border-amber-100/10 bg-[linear-gradient(90deg,rgba(251,191,36,0.22),rgba(255,251,235,0.06),rgba(251,191,36,0.22))]" />
            <div className="flex h-4 items-center justify-center gap-1 px-2">
              {Array.from({ length: 3 }, (_, dotIndex) => (
                <div key={dotIndex} className="h-1.5 w-1.5 rounded-full bg-amber-100/40" />
              ))}
            </div>
            <div className="h-2 border-t border-amber-100/10 bg-[linear-gradient(90deg,rgba(251,191,36,0.22),rgba(255,251,235,0.06),rgba(251,191,36,0.22))]" />
          </div>
        ))}
      </div>
      <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 justify-center px-3">
        <div
          className="rounded-full border border-amber-700/22 bg-amber-50/92 px-2.5 py-1 text-[8px] font-semibold uppercase tracking-[0.16em] text-amber-900 shadow-[0_4px_12px_rgba(245,158,11,0.14)]"
        >
          {label}
        </div>
      </div>
    </div>
  );
}

export default function VideoPrimaryLaneRenderer({ lane, items, viewport, syncBadge }: LaneRendererProps): JSX.Element {
  const tone = productTone(lane.productType);
  const visibleItems = visibleLaneItems(items, viewport);
  const overlaps = overlapCounts(visibleItems);
  const laneMetadata = ((lane as { metadata?: Record<string, unknown> }).metadata ?? {}) as Record<string, unknown>;

  return (
    <div className="relative h-full min-h-[48px]" data-lane-product="video_primary" data-lane-id={lane.id}>
      <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-sky-200/10" />
      {visibleItems.length > 0 ? (
        visibleItems.map(({ item, leftPx, widthPx }) => {
          const familyId = readStringMeta(item, "familyId");
          const memberId = readStringMeta(item, "memberId");
          const proxyStatus = readStringMeta(item, "proxyStatus");
          const preferredProxyKind = readStringMeta(item, "preferredProxyKind");
          const proxyContractState = readStringMeta(item, "proxyContractState");
          const offlineState = readStringMeta(item, "offlineState");
          const analysisState = readStringMeta(item, "analysisState");
          const itemMetadata = ((item as { metadata?: Record<string, unknown> }).metadata ?? {}) as Record<string, unknown>;
          const growingFile = itemMetadata.growingFile === true;
          const usingDeprecatedProxyFallback = itemMetadata.usingDeprecatedProxyFallback === true;
          const metadataRichnessScore = readNumberMeta(item, "metadataRichnessScore");
          const decodePerformanceScore = readNumberMeta(item, "decodePerformanceScore");
          const displayAspectRatio = readNumberMeta(item, "displayAspectRatio");
          const mediaUrl = readStringMeta(item, "mediaUrl");
          const label = "label" in item ? item.label : item.id;
          const density = itemVisualDensity(widthPx);
          const overlapCount = overlaps.get(item.id) ?? 1;
          const frameTicks = density === "regular" ? 5 : density === "compact" ? 3 : 2;
          const itemDurationSec = Math.max(0.1, item.endSec - item.startSec);
          const showThumbnails = density !== "micro" && typeof mediaUrl === "string" && mediaUrl.length > 0;
          const isProxyGenerating = proxyStatus === "queued" || proxyStatus === "generating";

          return (
            <div
              key={item.id}
              data-visual-lane-item-id={item.id}
              className="absolute top-1/2 flex h-10 -translate-y-1/2 items-center overflow-hidden rounded-xl border border-sky-500/28 bg-[linear-gradient(135deg,rgba(186,230,253,0.94),rgba(125,211,252,0.86))] px-3 shadow-[0_8px_20px_rgba(14,165,233,0.18)]"
              style={{ left: leftPx, width: widthPx }}
              title={label}
            >
              {showThumbnails ? <VideoThumbnailBed src={mediaUrl} durationSec={itemDurationSec} /> : null}
              {isProxyGenerating ? <VideoGeneratingOverlay widthPx={widthPx} status={proxyStatus} /> : null}
              {!showThumbnails ? (
                <div className="absolute inset-y-0 left-2 flex items-center gap-[3px] opacity-65">
                  {Array.from({ length: frameTicks }, (_, index) => (
                    <div key={index} className="h-5 w-1 rounded-full bg-sky-900/40" />
                  ))}
                </div>
              ) : null}
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(255,255,255,0.16),rgba(255,255,255,0.02),rgba(2,6,23,0.12))]" />
              <div className={density === "micro" ? "absolute inset-x-2 top-1/2 z-[1] flex -translate-y-1/2 justify-end" : "relative z-[1] min-w-0 pl-6"}>
                {density !== "micro" ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <span className="rounded-full border border-sky-700/18 bg-sky-50/92 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.14em] text-sky-900">
                        Video
                      </span>
                      <div className="truncate text-[11px] font-semibold text-sky-950">{label}</div>
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5 overflow-hidden">
                      <span className="truncate rounded-full border border-sky-800/14 bg-sky-950/10 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.14em] text-sky-950/82">
                        {[familyId, memberId].filter(Boolean).join(" · ") || "video span"}
                      </span>
                      {displayAspectRatio != null ? stateChip(`${displayAspectRatio.toFixed(2)} ar`, "slate") : null}
                      {proxyStatus === "ready" && proxyContractState !== "deprecated_proxy_fallback" ? stateChip((preferredProxyKind ?? "proxy").replace(/_/g, " "), "sky") : null}
                      {proxyStatus === "queued" || proxyStatus === "generating" ? stateChip(`proxy ${proxyStatus}`, "amber") : null}
                      {contractStateChip(proxyContractState)}
                      {offlineState && offlineState !== "online" ? stateChip(offlineState, "rose") : null}
                      {growingFile ? stateChip("growing", "amber") : null}
                      {analysisState && analysisState !== "ready" ? stateChip(`analysis ${analysisState}`, "amber") : null}
                      {metadataRichnessScore != null && metadataRichnessScore < 0.65 ? stateChip("thin meta", "slate") : null}
                      {decodePerformanceScore != null && decodePerformanceScore < 0.5 ? stateChip("decode watch", "amber") : null}
                    </div>
                  </>
                ) : null}
              </div>
              {overlapCount > 1 ? (
                <div className="absolute right-1.5 top-1.5 z-[1] rounded-full border border-sky-700/16 bg-sky-50/92 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-[0.12em] text-sky-900">
                  x{overlapCount}
                </div>
              ) : null}
              {density === "micro" ? (
                <div className="absolute left-1.5 top-1.5 z-[1] rounded-full border border-sky-700/18 bg-sky-50/92 px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-[0.14em] text-sky-900">VID</div>
              ) : null}
            </div>
          );
        })
      ) : (
        <div className={["flex min-h-[52px] items-center justify-between gap-3 rounded-2xl border px-3 py-3", tone.emptyClassName].join(" ")}>
          <div>
            <div className="flex items-center gap-2">
              <div className="text-[11px] font-semibold text-sky-950">Video lane is standing by.</div>
              {readStringMeta({ ...lane, metadata: lane.metadata } as never, "proxyStatus") === "ready" && readStringMeta({ ...lane, metadata: lane.metadata } as never, "proxyContractState") !== "deprecated_proxy_fallback" ? stateChip((readStringMeta({ ...lane, metadata: lane.metadata } as never, "preferredProxyKind") ?? "proxy").replace(/_/g, " "), "sky") : null}
              {contractStateChip(readStringMeta({ ...lane, metadata: lane.metadata } as never, "proxyContractState"))}
              {readStringMeta({ ...lane, metadata: lane.metadata } as never, "offlineState") && readStringMeta({ ...lane, metadata: lane.metadata } as never, "offlineState") !== "online" ? stateChip(readStringMeta({ ...lane, metadata: lane.metadata } as never, "offlineState") ?? "offline", "rose") : null}
              {laneMetadata.growingFile === true ? stateChip("growing", "amber") : null}
              {(readNumberMeta({ ...lane, metadata: lane.metadata } as never, "metadataRichnessScore") ?? 1) < 0.65 ? stateChip("thin meta", "slate") : null}
            </div>
            <div className={["mt-1 text-[10px]", GLASS_SECONDARY_TEXT_CLASS].join(" ")}>
              {syncBadge?.reason ?? "Active playback projection will paint clip spans here once a video member is resolved."}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1.5 opacity-80">
            {Array.from({ length: 9 }, (_, index) => (
              <div key={index} className="h-4 w-5 rounded border border-current/18 bg-current/10" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}